import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Select, Space } from "antd";
import { DotVerticalIcon } from "assets/icons";
import moment from "moment";

import AppButton from "../../../components/button";
import ModalForm from "../../../components/forms/components/modalForm";
import AppTable from "../../../components/table";
import {
  ADDRESS_STATUS,
  CHANNEL_NAME,
  STATUS_TYPE,
  SUPPLIERS,
  USER_TYPE,
} from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import { setHeaderTitle, setIsRefresh } from "../../../stores/slices/layout";
import {
  doDeleteAddress,
  doGetAddressPage,
  doUpdateAddressStatus,
} from "../../../stores/slices/numbers";
import { numOfEls } from "../../../stores/slices/numbers";
import { getUserDetail } from "../../../stores/slices/user";

import NumberAssignForm from "./numberAssignForm";
import NumberForm from "./numberForm";
import NumberOperatorAssignForm from "./numberOperatorAssignForm";

const NumberPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const authInfo = useSelector(AuthInfo);
  const userInfo = useSelector(getUserDetail);
  const noOfElements = useSelector(numOfEls);

  const [columns, setColumns] = useState([]);
  const [isView, setIsView] = useState(false);
  const [isShowAssignPopup, setIsShowAssignPopup] = useState(false);
  const [isShowOperatorAssignPopup, setIsShowOperatorAssignPopup] =
    useState(false);
  const [numberSelected, setNumberSelected] = useState(null);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [selectedNumberDelete, setSelectedNumberDelete] = useState();
  const [selectedNumberUnAssigned, setSelectedNumberUnAssigned] = useState();
  const [isOpenModalUnAssigned, setIsOpenModalUnAssigned] = useState();
  const [selectedChangeStatus, setSelectedChangeStatus] = useState();
  useEffect(() => {
    dispatch(setHeaderTitle("numbers"));

    setSearchParam({
      sortField: ["createdTime"],
      sortDirection: "DESC",
      pageNumber: 1,
      pageSize: 10,
    });

    let statusOpts = [
      {
        text: t("addressStatus.assigned"),
        label: t("addressStatus.assigned"),
        value: ADDRESS_STATUS.ASSIGNED,
      },
      {
        text: t("addressStatus.returned"),
        label: t("addressStatus.returned"),
        value: ADDRESS_STATUS.RETURNED,
      },
    ];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      statusOpts = [
        ...statusOpts,
        {
          text: t("addressStatus.available"),
          label: t("addressStatus.available"),
          value: ADDRESS_STATUS.AVAILABLE,
        },
        {
          text: t("addressStatus.depleted"),
          label: t("addressStatus.depleted"),
          value: ADDRESS_STATUS.DEPLETED,
        },
        {
          text: t("addressStatus.quarantined"),
          label: t("addressStatus.quarantined"),
          value: ADDRESS_STATUS.QUARANTINED,
        },
        {
          text: t("addressStatus.reserved"),
          label: t("addressStatus.reserved"),
          value: ADDRESS_STATUS.RESERVED,
        },
      ];
    }
    let statusOptsSort = statusOpts.sort((a, b) => {
      return a.value.toLowerCase() < b.value.toLowerCase()
        ? -1
        : a.value.toLowerCase() > b.value.toLowerCase()
        ? 1
        : 0;
    });
    setStatusOptions(statusOptsSort);
  }, [authInfo]);

  const renderProviders = providerList => {
    let newProviderList = [];
    const list = Object.entries(providerList);
    list?.map(([key, value]) => {
      if (value) {
        newProviderList.push(key);
      }
    });

    const resultString = newProviderList
      ?.map(key => {
        return t("channelProvider." + key);
      })
      .join(", ");
    return resultString ? resultString : "-";
  };

  const handleStatusOptions = record => {
    let options = [...statusOptions];
    options.map(item => {
      item.disabled =
        (record.status !== ADDRESS_STATUS.ASSIGNED &&
          item.value === ADDRESS_STATUS.RETURNED) ||
        (authInfo.type !== USER_TYPE.SUPER_ADMIN &&
          record.status === ADDRESS_STATUS.RETURNED &&
          item.value === ADDRESS_STATUS.AVAILABLE);
    });
    return options;
  };

  const [searchParam, setSearchParam] = useState();

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleSelectStatusChange = (value, record) => {
    if (
      value.key === ADDRESS_STATUS.ASSIGNED &&
      authInfo.type === USER_TYPE.SUPER_ADMIN
    ) {
      setNumberSelected(record);
      setIsShowAssignPopup(true);
    } else {
      setSelectedChangeStatus(value.key);
      setSelectedNumberUnAssigned(record);
      setIsOpenModalUnAssigned(true);
    }
  };

  const handleChangeStatusNumber = async (key, record) => {
    let params = {
      serviceStatus: key,
      workspaceId: record.workspace?.id,
    };

    if (
      key === ADDRESS_STATUS.AVAILABLE ||
      key === ADDRESS_STATUS.DEPLETED ||
      key === ADDRESS_STATUS.QUARANTINED ||
      key === ADDRESS_STATUS.RESERVED
    ) {
      // Check if the number is in the above state to set the voice service to false
      params = {
        ...params,
        voice: false,
      };
    }

    setIsStatusChanging(true);
    const { payload } = await dispatch(
      doUpdateAddressStatus({ id: record.id, params })
    );
    setIsOpenModalUnAssigned(false);
    setSelectedNumberUnAssigned();
    setSelectedChangeStatus();
    if (payload && payload.status === 200) {
      setIsStatusChanging(false);
      dispatch(setIsRefresh(uuid()));
    } else {
      setIsStatusChanging(false);
    }
  };

  const handleDelete = async record => {
    const { payload } = await dispatch(doDeleteAddress(record.id.toString()));
    setIsOpenModalDelete(false);
    setSelectedNumberDelete();
    if (payload && payload.status === 200) {
      if (searchParam.pageNumber > 1 && noOfElements === 1) {
        const newSearchParam = {
          ...searchParam,
          pageNumber: searchParam.pageNumber - 1,
        };
        setSearchParam(newSearchParam);
      }
      dispatch(setIsRefresh(uuid()));
      setIsDeleting(false);
    } else {
      setIsDeleting(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      filters.workspaceName &&
        (newSearchParam = {
          ...newSearchParam,
          workspaceName: filters.workspaceName[0],
        });
    }
    filters.supplier &&
      (newSearchParam = {
        ...newSearchParam,
        suppliers: filters.supplier,
      });
    filters.address &&
      (newSearchParam = {
        ...newSearchParam,
        address: filters.address[0],
      });
    filters.status &&
      (newSearchParam = {
        ...newSearchParam,
        statuses: filters.status,
      });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortField: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortField: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      pageNumber: 1,
      pageSize: 10,
    };
    setSearchParam(newSearchParam);
  };

  const handleFormatDateLocale = date => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date)
        .tz(userInfo?.timeZone || moment.tz.guess())
        .locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const actionOptionItems = record => {
    return [
      {
        key: "1",
        label: <span>{t("numbers.viewAndEdit")}</span>,
        icon: (
          <span style={{ marginInlineEnd: 8 }}>
            <EyeOutlined />
          </span>
        ),
        disabled: record.status === STATUS_TYPE.DELETE,
        onClick: () => {
          setIsView(true);
          setNumberSelected(record);
          setIsOpenForm(true);
        },
      },
      {
        key: "2",
        danger: true,
        label: <span>{t("numbers.delete")}</span>,
        icon: (
          <span style={{ marginInlineEnd: 8 }}>
            <DeleteOutlined />
          </span>
        ),
        disabled:
          authInfo.type !== USER_TYPE.SUPER_ADMIN ||
          record.status === ADDRESS_STATUS.ASSIGNED ||
          record.status === ADDRESS_STATUS.RETURNED,
        onClick: () => {
          setSelectedNumberDelete(record);
          setIsOpenModalDelete(true);
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [
      {
        title: t("numbers.number"),
        width: 60,
        dataIndex: "address",
        key: "address",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " + t("numbers.number").toString().toLowerCase(),
        ellipsis: {
          showTitle: false,
        },
        render: (_, { address }) => <>{address}</>,
      },
    ];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols = [
        {
          title: t("messageTemplates.workspace"),
          width: 80,
          dataIndex: ["workspace", "name"],
          key: "workspaceName",
          sorter: true,
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("numbers.workspace").toString().toLowerCase(),
          ellipsis: {
            showTitle: true,
          },
          render: (_, { workspace }) => (
            <>{workspace?.name ? workspace.name : "-"}</>
          ),
        },
        ...cols,
        {
          title: t("numbers.supplier"),
          width: 50,
          dataIndex: "supplier",
          key: "supplier",
          filters: [
            {
              text: t("suppliers.SINCH"),
              value: SUPPLIERS.SINCH,
            },
            {
              text: t("suppliers.TWILO"),
              value: SUPPLIERS.TWILO,
            },
          ],
          render: (_, { supplier }) => (
            <>
              {supplier === SUPPLIERS.SINCH
                ? t("suppliers.SINCH")
                : supplier === SUPPLIERS.TWILO
                ? t("suppliers.TWILO")
                : supplier}
            </>
          ),
        },
      ];
    }
    cols = [
      ...cols,
      {
        title: t("numbers.protocols"),
        width: 100,
        dataIndex: "protocols",
        key: "protocols",
        ellipsis: {
          showTitle: true,
        },
        render: (_, { configuration }) => {
          return renderProviders(configuration);
        },
      },
      {
        title: t("numbers.channel"),
        width: 50,
        dataIndex: ["channel", "name"],
        key: "channel",
        ellipsis: {
          showTitle: true,
        },
        render: (_, { channel }) => (
          <>
            {channel?.status !== STATUS_TYPE.DELETE && channel?.name
              ? channel?.name === CHANNEL_NAME.BULKSMS
                ? t("channelName.BULKSMS")
                : channel?.name
              : "-"}
          </>
        ),
      },
      {
        title: t("numbers.dateUpdated"),
        width: 60,
        dataIndex: "updatedTime",
        key: "updatedTime",
        sorter: true,
        render: (_, record) => (
          <>
            {record.updatedTime
              ? handleFormatDateLocale(record.updatedTime)
              : record.createdTime
              ? handleFormatDateLocale(record.createdTime)
              : "-"}
          </>
        ),
      },
      {
        title: t("numbers.status"),
        width: 50,
        dataIndex: "status",
        key: "status",
        sorter: true,
        filters: statusOptions,
        render: (_, record) => (
          <>
            <Select
              labelInValue
              defaultValue={record.status}
              style={{
                width: "100%",
              }}
              onChange={e => {
                handleSelectStatusChange(e, record);
              }}
              onFocus={() => {
                handleStatusOptions(record);
              }}
              options={statusOptions}
              disabled={
                record.status === ADDRESS_STATUS.RETURNED &&
                authInfo.type !== USER_TYPE.SUPER_ADMIN
              }
            />
          </>
        ),
      },
      {
        title: t("numbers.actions"),
        width: 40,
        key: "operation",
        render: (_, record) => (
          <Space size="small">
            <Dropdown
              menu={{
                items: actionOptionItems(record),
              }}
              overlayClassName="workspaceActionDropdown"
              placement="bottomRight"
              trigger={"click"}
            >
              <Button
                type="text"
                shape="circle"
                size="middle"
                icon={<DotVerticalIcon />}
              />
            </Dropdown>
          </Space>
        ),
      },
    ];
    setColumns(cols);
  };

  useEffect(() => {
    generateTableColumns();
  }, [noOfElements, statusOptions]);

  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("numbers.deleteNumberTitle")}
        </span>
      </div>
    );
  };
  const handleTitleUnAssigned = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {selectedChangeStatus &&
            t(`numbers.message.${selectedChangeStatus.toLowerCase()}Title`)}
        </span>
      </div>
    );
  };

  return (
    <div style={numberPageStyles.mainContainer}>
      <div style={numberPageStyles.buttonContainer}>
        <AppButton
          label={t("button.add")}
          color={"primary"}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
              setIsView(false);
              setNumberSelected(null);
              setIsOpenForm(true);
            } else {
              setIsShowOperatorAssignPopup(true);
            }
          }}
          style={numberPageStyles.buttonAdd}
        />
      </div>
      {columns && (
        <AppTable
          api={doGetAddressPage(searchParam)}
          columns={columns}
          searchParam={searchParam}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          handleTableChange={handleTableChange}
          setSearchParam={setSearchParam}
        />
      )}

      <ModalForm
        title={
          numberSelected && !isView
            ? t("numbers.updatePopupTitle")
            : numberSelected && isView
            ? t("numbers.viewPopupTitle")
            : t("numbers.addNewPopupTitle")
        }
        isOpen={isOpenForm}
        destroyOnClose={true}
        labelSubmit={t("label.save")}
        footer={false}
        onCancel={() => {
          setIsOpenForm(false);
        }}
        customWidth={800}
        maskClosable={false}
      >
        <NumberForm
          isViewForm={isView}
          userType={authInfo.type}
          workspaceId={authInfo?.workspaceId}
          numberSelected={numberSelected}
          loadingList={loadingList}
          setLoadingList={setLoadingList}
          statusOptions={statusOptions}
          onCancel={() => {
            setIsOpenForm(false);
            setNumberSelected(null);
          }}
          onSuccess={() => {
            dispatch(setIsRefresh(uuid()));
            setIsOpenForm(false);
          }}
          onUpdate={() => {
            setIsView(false);
          }}
        />
      </ModalForm>

      <ModalForm
        title={t("numbers.message.assignedTitle")}
        isOpen={isShowAssignPopup}
        destroyOnClose={true}
        labelSubmit={t("label.save")}
        footer={false}
        onCancel={() => {
          setNumberSelected(null);
          setIsShowAssignPopup(false);
        }}
        customWidth={500}
        maskClosable={false}
      >
        <NumberAssignForm
          numberSelected={numberSelected}
          loadingList={loadingList}
          setLoadingList={setLoadingList}
          onCancel={() => {
            setNumberSelected(null);
            setIsShowAssignPopup(false);
          }}
          onSuccess={() => {
            setNumberSelected(null);
            setIsShowAssignPopup(false);
            dispatch(setIsRefresh(uuid()));
          }}
        />
      </ModalForm>

      <ModalForm
        title={t("numbers.message.assignedNewTitle")}
        isOpen={isShowOperatorAssignPopup}
        destroyOnClose={true}
        footer={false}
        onCancel={() => {
          setIsShowOperatorAssignPopup(false);
        }}
        customWidth={500}
        maskClosable={false}
      >
        <NumberOperatorAssignForm
          onCancel={() => {
            setNumberSelected(null);
            setIsShowOperatorAssignPopup(false);
          }}
          onSuccess={() => {
            setNumberSelected(null);
            setIsShowOperatorAssignPopup(false);
            setSearchParam({ ...searchParam, pageNumber: 1 });
            dispatch(setIsRefresh(uuid()));
          }}
        />
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalDelete}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalDelete(false);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalDelete(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.delete")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleDelete(selectedNumberDelete);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("numbers.deleteNumberMsg")}
        </div>
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalUnAssigned}
        type="confirm"
        title={handleTitleUnAssigned()}
        onCancel={() => {
          setIsOpenModalUnAssigned(false);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isStatusChanging}
            disabled={isStatusChanging}
            htmlType="button"
            onClick={() => {
              setIsOpenModalUnAssigned(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.confirm")}
            loading={isStatusChanging}
            disabled={isStatusChanging}
            onClick={() => {
              setIsStatusChanging(true);
              handleChangeStatusNumber(
                selectedChangeStatus,
                selectedNumberUnAssigned
              );
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {selectedChangeStatus &&
            t(`numbers.message.${selectedChangeStatus.toLowerCase()}Msg`)}
        </div>
      </ModalForm>
    </div>
  );
};

const numberPageStyles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    // gap: "24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
    marginBottom: "24px",
  },
  buttonAdd: {
    minWidth: "auto",
    display: "flex",
    alignItems: "center",
  },
  "@media (max-width: 320px)": {
    buttonContainer: {
      flexDirection: "columns-reverse !important",
    },
  },
};

export default NumberPage;
